import { SupabaseClient, createClient } from "@supabase/supabase-js";

import { Database } from "@/data/supabase.types";
import { SubscriptionStatusResponseEnum } from "@/app/api/check-subscription/subscription-response.types";
import { getHostname } from "@/components/common/utils";
import { logException } from "@/analytics/sentry";

export interface Subscription {
  comedian_id: string;
  created_at: string;
  id: string;
  price_id: string;
  tier: number;
}

export type UserSubscriptionRow =
  Database["public"]["Tables"]["user_subscriptions"]["Row"];

const stripeProductsTable =
  process.env.VERCEL_ENV === "production"
    ? "stripe_products"
    : "stripe_products_dev";

export const subscriptionsTable =
  process.env.VERCEL_ENV === "production"
    ? "user_subscriptions"
    : "user_subscriptions_dev";

export async function getComedianStripeProducts(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<Subscription[]> {
  console.log(
    `Retrieving Stripe products for comedian ${comedianId} from table ${stripeProductsTable}`
  );

  if (!comedianId) {
    console.error("getComedianStripeProducts called with empty comedianId");
    throw new Error("Comedian ID is required");
  }

  try {
    const { data, error } = await supabase
      .from(stripeProductsTable)
      .select("*")
      .eq("comedian_id", comedianId);

    if (error) {
      console.error(
        `Database error retrieving products for comedian ${comedianId}:`,
        error
      );
      throw new Error(`Database error: ${error.message}`);
    }

    if (data) {
      console.log(`Found ${data.length} products for comedian ${comedianId}`);
      if (data.length > 0) {
        data.forEach((product, index) => {
          console.log(`Product ${index + 1}:`, {
            id: product.id,
            price_id: product.price_id,
            tier: product.tier,
          });
        });
      }
      return data;
    }

    console.log(`No products found for comedian ${comedianId}`);
    return [];
  } catch (error) {
    console.error(
      `Unexpected error in getComedianStripeProducts for comedian ${comedianId}:`,
      error
    );
    throw new Error(
      `Issue getting stripe products for comedian ${comedianId}: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function getStripeProductToComedianMap(
  supabase: SupabaseClient<Database>
): Promise<{
  [key: string]: {
    comedian: {
      id: string;
      display_name: string;
    };
  };
}> {
  const { data, error } = await supabase.from(stripeProductsTable).select(`
      id,
      comedian:comedians!inner (
        id,
        display_name
      )
    `);
  if (error) {
    console.error(error);
  } else if (data) {
    const d: {
      [key: string]: {
        comedian: {
          id: string;
          display_name: string;
        };
      };
    } = {};
    data.forEach(
      (i) =>
        (d[i.id] = {
          comedian: {
            id: i.comedian?.id || "",
            display_name: i.comedian?.display_name || "",
          },
        })
    );
    return d;
  } else if (!error && !data) {
    return {};
  }
  throw new Error(`Issue getting stripe product to comedian map`);
}

// Checks if a user is subscribed to a comedian and the status of the subscription
export async function userIsSubscribedToComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    return false;
  }

  try {
    const subscriptionProductData = await getComedianStripeProducts(
      supabase,
      comedianId
    );

    // Case in which the comedian doesn't have any products
    if (!subscriptionProductData.length) {
      return false;
    }

    // Retrieve subscription status for this user
    const hostname = getHostname();
    const subscriptionStatusCheckResponse = await fetch(
      `${hostname}/api/check-subscription`,
      {
        method: "POST",
        body: JSON.stringify({
          productIdReference: subscriptionProductData[0].id,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (subscriptionStatusCheckResponse.ok) {
      const subscriptionStatusCheck =
        await subscriptionStatusCheckResponse.json();
      if (
        subscriptionStatusCheck.message ===
        SubscriptionStatusResponseEnum.SUBSCRIPTION_VALID
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      throw new Error(await subscriptionStatusCheckResponse.text());
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error while validating subscription status: ${err}`);
  }
}

export type SubscriptionWithComedian = {
  stripe_subscription_id: string;
  stripe_product: {
    price_id: string;
    tier: number;
    comedian: {
      id: string;
      display_name: string | null;
      slug: string;
      about_img: string | null;
    } | null;
  } | null;
};

export async function getUserSubscriptionsAndTheirComedians(
  supabase: SupabaseClient<Database>
): Promise<SubscriptionWithComedian[]> {
  let filter: {
    user_id?: string;
  } = {};
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.user) {
    filter["user_id"] = session.user.id;
  }
  const { data, error } = await supabase
    .from(subscriptionsTable)
    .select(
      `
      stripe_subscription_id,
      stripe_product:${stripeProductsTable}!inner (
        price_id,
        tier,
        comedian:comedians!inner (
          id,
          display_name,
          slug,
          about_img
        )
      )
    `
    )
    .match(filter);

  if (error) {
    logException(error);
    throw error;
  }

  return data.map((item) => ({
    stripe_subscription_id: item.stripe_subscription_id,
    stripe_product: Array.isArray(item.stripe_product)
      ? item.stripe_product[0] || null
      : item.stripe_product,
  })) as SubscriptionWithComedian[];
}
export async function getUserSubscriptionId(
  userId: string,
  stripeProductIdReference: string
) {
  const supabaseAdmin = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
    process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
    { auth: { persistSession: false, autoRefreshToken: false } }
  );

  const { data, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .select("stripe_subscription_id")
    .match({ user_id: userId, stripe_product_id: stripeProductIdReference })
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (data) {
    return data.stripe_subscription_id;
  } else if (!error && !data) {
    return "";
  }
  throw new Error(
    `Error attemping to fetch subscription id for user ${userId}`
  );
}

export async function addSubscriptionToUser(
  supabaseAdmin: SupabaseClient<Database>,
  userID: string,
  subscriptionProductID: string,
  stripeSubscriptionID: string
) {
  if (!stripeSubscriptionID) {
    `Error attemping to add subscription because to user because it's missing the stripeSubscriptionID`;
  }

  if (!userID) {
    `Error attemping to add subscription ${stripeSubscriptionID} to user because userID is empty`;
  }

  if (!subscriptionProductID) {
    `Error attemping to add subscription ${stripeSubscriptionID} to user because subscriptionProductID is empty`;
  }

  const { status, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .upsert(
      {
        user_id: userID,
        stripe_product_id: subscriptionProductID,
        stripe_subscription_id: stripeSubscriptionID,
      },
      {
        onConflict: "stripe_subscription_id",
        ignoreDuplicates: false,
        count: "exact",
      }
    )
    .select();

  if (error) {
    console.error(error);
  } else if (status === 201) {
    return;
  }
  throw new Error(
    `Error attemping to add subscription ${stripeSubscriptionID} to user ${userID}`
  );
}

// Note: this function is called (and should only be called) by a server function
// Never on the client
export async function removeSubscriptionForUser(
  userID: string,
  stripeSubscriptionID: string
) {
  if (!userID) {
    throw new Error(
      "Cannot proceed with purging subscription as the userID is missing"
    );
  }

  if (!stripeSubscriptionID) {
    throw new Error(
      "Cannot proceed with purging subscription as the stripeSubscriptionID is missing"
    );
  }
  const supabaseAdmin = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
    process.env.SUPABASE_SERVICE_ROLE_KEY ?? "",
    { auth: { persistSession: false, autoRefreshToken: false } }
  );
  const { count, status, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .delete({ count: "exact" })
    .match({
      user_id: userID,
      stripe_subscription_id: stripeSubscriptionID,
    });
  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 204 && count === 1) {
    return true;
  } else {
    throw new Error(
      `Error deleting subscription for user with id ${userID}. Row count was: ${count}.`
    );
  }
}

export async function getSubscriptionPriceForComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  try {
    const { data } = await supabase
      .from("comedian_subscriptions")
      .select("price")
      .eq("comedian_id", comedianId)
      .maybeSingle()
      .throwOnError();

    return data?.price;
  } catch (error) {
    logException(error);
    return null;
  }
}
